import React, { PropsWithChildren, useEffect, useState } from 'react';
import { FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { PASSWORD_MANAGER_IGNORE } from '../../../constants';

const TextInput = (
  props: PropsWithChildren<{
    name: string;
    label: string;
    type?: 'text' | 'url' | 'email' | 'tel' | 'password';
    defaultValue?: string;
    maxLength?: number;
    minLength?: number;
    required?: boolean;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    invalid?: boolean;
    autoComplete?: string;
    onBlur?: () => void;
    formFeedback?: string;
    disabled?: boolean;
    // Suppresses password-manager in-field icons — see
    // PASSWORD_MANAGER_IGNORE. Fields worth autofilling leave it off.
    ignorePasswordManagers?: boolean;
  }>
) => {
  const [value, setValue] = useState(
    !!props.defaultValue
      ? !!props.maxLength
        ? props.defaultValue.slice(0, props.maxLength - 1)
        : props.defaultValue
      : ''
  );
  const [hasFocus, setHasFocus] = useState(false);
  const [hasBlur, setHasBlur] = useState(false);

  useEffect(() => {
    if (!!props.value && props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);

  return (
    <FormGroup floating>
      <Input
        name={props.name}
        autoComplete={props.autoComplete}
        id={props.name}
        data-testid={props.name}
        {...(props.ignorePasswordManagers ? PASSWORD_MANAGER_IGNORE : {})}
        placeholder={props.label}
        type={props.type ?? 'text'}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (props.onChange) {
            props.onChange(e);
          }
        }}
        onBlur={() => {
          setHasBlur(true);
          setHasFocus(false);
          if (props.onBlur) {
            props.onBlur();
          }
        }}
        onFocus={() => {
          setHasFocus(true);
          setHasBlur(false);
        }}
        maxLength={!!props.maxLength ? props.maxLength : undefined}
        minLength={!!props.minLength ? props.minLength : undefined}
        disabled={props.disabled}
        invalid={
          !!props.invalid ||
          (hasBlur && !hasFocus && props.required && !value.trim())
        }
        valid={
          hasBlur &&
          !hasFocus &&
          !!value.trim() &&
          (!props.maxLength || value.length <= props.maxLength)
        }
        required={props.required}
      />
      <Label for={props.name} required={props.required}>
        {props.label}
      </Label>
      {((hasBlur || !!props.formFeedback) && props.required && !value.trim()) ||
      (!!props.invalid && !!props.formFeedback) ? (
        <FormFeedback className="text-left">
          {!!props.formFeedback ? (
            <>{props.formFeedback}</>
          ) : (
            <>{props.label} is required.</>
          )}
        </FormFeedback>
      ) : null}
      {props.children}
    </FormGroup>
  );
};

export default TextInput;
